// 價格顯示（Kevin 2026-10-19 拍板）：千位逗號＋小數點後兩位，例 HK$1,299.00
export function formatPrice(cents: number): string {
  return `HK$${(cents / 100).toLocaleString('en-HK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Display-only transform: present the brand token in guillemets
 * («X-1493» / «X1493», original token preserved) without touching stored
 * data. Idempotent — already-wrapped tokens are left unchanged.
 */
export function brandifyText(text: string): string {
  return text.replace(/X-?1493/g, (match: string, offset: number, s: string) => {
    if (s[offset - 1] === '«' && s[offset + match.length] === '»') return match;
    return `«${match}»`;
  });
}

/**
 * 將 HTML 轉成單行純文字（產品卡摘要等位置用）：
 * 區塊 tag → 空格、剝走其餘 tag、解 entity、收斂空白。
 * 冇 tag 嘅純文字原樣通過（向後兼容）。
 */
export function stripHtmlToText(input: string): string {
  const text = String(input || '');
  if (!/<[a-z][\s\S]*>/i.test(text)) return text.trim();
  let out = text
    .replace(/<\s*br\s*\/?\s*>/gi, ' ')
    .replace(/<\/\s*(p|div|li|h[1-6]|blockquote|ul|ol)\s*>/gi, ' ')
    .replace(/<\s*li[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, '');
  out = out
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return out.replace(/\s+/g, ' ').trim();
}

export function formatPriceDecimal(cents: number): string {
  return `HK$${(cents / 100).toLocaleString('en-HK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const VISITOR_ID_KEY = 'x1493_visitor_id';
const SESSION_ID_KEY = 'x1493_session_id';
const VISIT_ID_KEY = 'x1493_visit_id';
const VISIT_LAST_ACTIVE_KEY = 'x1493_visit_last_active';
const VISIT_NEW_FLAG_KEY = 'x1493_visit_new';
// 到訪邊界：30 分鐘休止後視為新到訪（Google Analytics 同款口徑）
const VISIT_IDLE_MS = 30 * 60 * 1000;

/**
 * 訪客級 ID（localStorage 持久）——同瀏覽器跨日不變，直到用戶清除 storage。
 */
export function getVisitorId(): string {
  if (typeof window === 'undefined') {
    return `ssr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  const persistentId = localStorage.getItem(VISITOR_ID_KEY);
  if (persistentId) {
    sessionStorage.setItem(SESSION_ID_KEY, persistentId);
    return persistentId;
  }

  const existing = sessionStorage.getItem(SESSION_ID_KEY);
  if (existing) {
    localStorage.setItem(VISITOR_ID_KEY, existing);
    return existing;
  }

  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  sessionStorage.setItem(SESSION_ID_KEY, id);
  localStorage.setItem(VISITOR_ID_KEY, id);
  return id;
}

/**
 * @deprecated 舊名（語義誤導——實為持久 visitor ID，非 session）；改用 getVisitorId()。
 */
export function generateSessionId(): string {
  return getVisitorId();
}

/**
 * 到訪級 ID（sessionStorage）——每次到訪一個新 ID，30 分鐘休止後重建。
 * 注意：休止判定只在活躍 tab 內有效（跨 tab 各自為一次到訪，共用同一 visitor ID）。
 */
export function getVisitId(): string {
  if (typeof window === 'undefined') {
    return `ssr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  const now = Date.now();
  const lastActive = Number(sessionStorage.getItem(VISIT_LAST_ACTIVE_KEY) || 0);
  let visitId = sessionStorage.getItem(VISIT_ID_KEY);
  if (!visitId || (lastActive > 0 && now - lastActive > VISIT_IDLE_MS)) {
    visitId = `v-${now}-${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(VISIT_ID_KEY, visitId);
    sessionStorage.setItem(VISIT_NEW_FLAG_KEY, '1');
  }
  sessionStorage.setItem(VISIT_LAST_ACTIVE_KEY, String(now));
  return visitId;
}

/**
 * 一次性旗標：getVisitId() 剛建立新到訪時為 true（讀後即清），
 * 供 analytics 在新到訪的第一個事件前補發 session_entry。
 */
export function consumeNewVisitFlag(): boolean {
  if (typeof window === 'undefined') return false;
  if (!sessionStorage.getItem(VISIT_NEW_FLAG_KEY)) return false;
  sessionStorage.removeItem(VISIT_NEW_FLAG_KEY);
  return true;
}

export function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('852')) return digits;
  if (digits.startsWith('0') && digits.length === 8) {
    return `852${digits.slice(1)}`;
  }
  if (digits.length === 8) {
    return `852${digits}`;
  }
  return digits;
}

export function maskPhone(phone: string): string {
  if (!phone || phone.length < 8) return phone;
  return `${phone.slice(0, 5)}***${phone.slice(-2)}`;
}

export function normalizeReferralCode(code: string): string {
  const cleaned = String(code || '').trim().toUpperCase();
  // Accept both "R-XXXXXX" and "XXXXXX"; always store canonical "R-XXXXXX"
  if (/^R-[A-Z0-9]{6}$/.test(cleaned)) return cleaned;
  if (/^[A-Z0-9]{6}$/.test(cleaned)) return `R-${cleaned}`;
  return cleaned;
}

export function displayReferralCode(code: string): string {
  return String(code || '').replace(/^R-/, '');
}

export function getReferralCode(): string | null {
  return localStorage.getItem('x1493_referral_code');
}

/**
 * 轉介歸因鏈：訪客帶活躍轉介碼時，任何分享面（複製/社交/QR）產生的
 * 連結都必須接上 ?r=，令再次分享照樣歸因給原轉介人（首次接觸優先，
 * 下游 RefCapture 不會覆蓋已存的碼）。
 */
export function appendReferralCode(url: string): string {
  const code = getReferralCode();
  if (!code || !url) return url;
  return `${url}${url.includes('?') ? '&' : '?'}r=${code}`;
}

export function setReferralCode(code: string) {
  localStorage.setItem('x1493_referral_code', normalizeReferralCode(code));
  localStorage.setItem('x1493_ref_captured_at', Date.now().toString());
}

export function getAuthToken(): string | null {
  return localStorage.getItem('x1493_auth_token');
}

export function setAuthToken(token: string) {
  localStorage.setItem('x1493_auth_token', token);
}

export function removeAuthToken() {
  localStorage.removeItem('x1493_auth_token');
}

export function getMember(): { phone: string; name?: string } | null {
  const raw = localStorage.getItem('x1493_member');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setMember(member: { phone: string; name?: string }) {
  localStorage.setItem('x1493_member', JSON.stringify(member));
}

export function removeMember() {
  localStorage.removeItem('x1493_member');
}

export function formatDateTime(timestamp?: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleString('zh-HK');
}

export function formatDate(timestamp?: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleDateString('zh-HK');
}
